/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2021, Bulb Inc. For more information, please email contact@hellobulb.com.
*/
import {
  Component, Inject, Input, OnDestroy, OnInit,
} from '@angular/core';
import { Location, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import {
  catchError, distinctUntilChanged, filter, map, switchMap, tap,
} from 'rxjs/operators';
import { SvgIconRegistry } from '@ngneat/svg-icon';

import { fasLockIcon } from 'IconLibrary/fontawesome/solid/lock';
import { fasUsersCogIcon } from 'IconLibrary/fontawesome/solid/users-cog';
import { faGlobeIcon } from 'IconLibrary/fontawesome/regular/globe';

import { AeroDialogService } from 'src/aero/aero-dialog/aero-dialog.service';
import { MessagesService } from '../messages.service';
import { ShareSettingsForm, SubjectViewModel, UnitPermissionsModel } from '../share-panel';
import { HttpService } from '../http.service';
import { animations } from './share-form.animations';
import { compareUnitPermissions } from '../utilities';
import { ShareTransition, UnitStateStore } from '../../../unit/unit-state.store';
import { ShareStateStore } from '../share-state.store';

enum PrivacyLevel {
  PRIVATE = 'Private',
  CUSTOM = 'Custom',
  PUBLIC = 'Public',
}

const PrivacyIcons = {
  PRIVATE: fasLockIcon.name,
  CUSTOM: fasUsersCogIcon.name,
  PUBLIC: faGlobeIcon.name,
};

/**
 * Partial definition of an account's SharingFeature data used by this component.
 */
interface SharingFeature {
  privateLinks: boolean,
  socialSharing: boolean,
}

/**
 * Partial definition of an account's PublishingFeature data used by this component.
 */
interface PublishingFeature {
  publicPublishing: boolean,
  maxPublishedPages: number,
  userPublishing: boolean,
  organizationPublishing: boolean,
  groupPublishing: boolean,
}

@Component({
  selector: 'bulb-share-form',
  providers: [Location, AeroDialogService, { provide: LocationStrategy, useClass: PathLocationStrategy }],
  templateUrl: './share-form.component.html',
  styleUrls: ['./share-form.component.scss'],
  standalone: false,
  animations,
})
export class ShareFormComponent implements OnInit, OnDestroy {
  @Input() unitId: string;

  @Input() compositeUnit: boolean;

  @Input() parentUnitId: string;

  public selectedUsers: SubjectViewModel[] = [];

  private ngUnsubscribe: Subscription = new Subscription();

  public settingsLoaded: string = 'hide';

  private isUpdatingFromApi = false;

  /**
   * True if user has free account.
   */
  public isFreeUser: boolean;

  public privacyOptions = Object.keys(PrivacyLevel).map((v, i) => ({
    code: v,
    name: `privacyLevel.${Object.values(PrivacyLevel)[i].toLowerCase()}`,
    icon: Object.values(PrivacyIcons)[i],
    disabled: false,
    disabledReason: '',
  }));

  public shareSettings: ShareSettingsForm = new UntypedFormGroup({
    id: new UntypedFormControl(''),
    privacyLevel: new UntypedFormGroup({
      level: new UntypedFormControl('PRIVATE'),
      inherited: new UntypedFormControl(PrivacyLevel.PRIVATE),
    }),
    permissions: new UntypedFormControl([]),
    privateShareLink: new UntypedFormControl(''),
    path: new UntypedFormControl(''),
  });

  /**
   * The SharingFeature settings for the current account.
   */
  public sharingFeature: SharingFeature;

  /**
   * The PublishingFeature settings for the current account.
   * @public
   */
  public publishingFeature: PublishingFeature;

  // Store the last settings for reference.
  // This is used to determine if the user has made any changes that require messages displays.
  private lastSettings: UnitPermissionsModel = {} as UnitPermissionsModel;

  // Store the last settings for reference.
  // This is used to determine if the user has made any changes that require messages displays.
  private updatedSettings: UnitPermissionsModel = {} as UnitPermissionsModel;

  /**
   * The initial privacy level state when the panel was opened.
   * @private
   */
  private initialPrivacyLevel: PrivacyLevel;

  private onChange: Observable<UnitPermissionsModel> = this.shareSettings.valueChanges
    .pipe(
      filter(() => !this.isUpdatingFromApi),
      distinctUntilChanged(compareUnitPermissions),
      // 🔹 Clean settings before API call
      map((settings: UnitPermissionsModel) => {

        const collectionItem = settings.permissions?.find(
          (s) => s.collection === true && s.permissionType === 'BULB_COL',
        );

        if (collectionItem?.parentGroupId) {
          settings = {
            ...settings,
            permissions: settings.permissions.filter(
              (s) => s.id !== collectionItem.parentGroupId,
            ),
          };
        }

        return settings;
      }),
      switchMap((settings: UnitPermissionsModel): Observable<UnitPermissionsModel> => this.httpSvc
        .savePermissions(this.unitId, settings)
        .pipe(
          tap(this.updateUrl.bind(this)),
          tap((resp: UnitPermissionsModel) => {
            this.isUpdatingFromApi = true;
            this.messagesSvc.showPrivacyChangeMessage(resp, this.lastSettings);
            // const parentId = settings.permissions
            //   ?.find((p) => p.permissionType === 'BULB_COL')
            //   ?.id;
            //
            // if (parentId != null) {
            //   this.parentUnitId = parentId;
            // }

            // window.dispatchEvent(new CustomEvent('refreshView'));
            // Update the view with the new settings, don't notify subscribers of the change.
            // For some reason, modal-select-input emits an event on each change,
            // while the other inputs do not. There is some discussion about this here:
            // https://github.com/angular/angular/issues/20230#issuecomment-818550963
            this.shareSettings.setValue(resp, {
              emitEvent: false,
            });
            this.isUpdatingFromApi = false;

            if (this.initialPrivacyLevel.toString() === PrivacyLevel.PRIVATE.toString().toUpperCase()
                && this.BulbAuthService.currentUser.position === 'STUDENT') {
              if (resp.privacyLevel.level !== PrivacyLevel.PRIVATE.toString().toUpperCase()) {
                this.shareStateStore.hasStudentShared = true;
              } else {
                this.shareStateStore.hasStudentShared = false;
              }
            }
          }),
          tap(this.refreshUserAndUpdateSharePermissions.bind(this)),
        )),
    );

  private publicOption: any;

  private customOption: any;

  private privateOption: any;

  constructor(
    private dialogService: AeroDialogService,
    private httpSvc: HttpService,
    private iconRegistry: SvgIconRegistry,
    private location: Location,
    private messagesSvc: MessagesService,
    private shareStateStore: ShareStateStore,
    private unitStateStore: UnitStateStore,
    @Inject('$location') private $location,
    @Inject('BulbAuthService') private BulbAuthService,
    @Inject('$rootScope') private $rootScope,
    @Inject('$state') private state,
  ) {
    this.iconRegistry.register([
      fasLockIcon,
      fasUsersCogIcon,
      faGlobeIcon,
    ]);

    this.publishingFeature = this.BulbAuthService.currentUser.features.PublishingFeature;
    this.sharingFeature = this.BulbAuthService.currentUser.features.SharingFeature;
    this.isFreeUser = this.BulbAuthService.currentUser.type === 'FREE';
  }

  private updateUrl(settings: UnitPermissionsModel) {
    const newPath = `/u/${settings.path}`;
    if (!this.location.isCurrentPathEqualTo(newPath)) {
      window.dispatchEvent(
        new CustomEvent('refreshView', {
          detail: {
            unitPath: settings.path,
          },
        }),
      );
    }
  }

  async ngOnInit(): Promise<void> {
    const publicOptionIndex = this.privacyOptions.findIndex((o) => o.code === 'PUBLIC');
    const customOptionIndex = this.privacyOptions.findIndex((o) => o.code === 'CUSTOM');
    const privateOptionIndex = this.privacyOptions.findIndex((o) => o.code === 'PRIVATE');

    this.publicOption = this.privacyOptions[publicOptionIndex];
    this.customOption = this.privacyOptions[customOptionIndex];
    this.privateOption = this.privacyOptions[privateOptionIndex];
    this.httpSvc.getPermissions(this.unitId)
      .subscribe((resp: UnitPermissionsModel) => {
        this.settingsLoaded = 'show';
        // Update the form with the current permissions from the server.
        this.lastSettings = resp;
        this.shareSettings.patchValue(resp);
        this.updateUserSharePermissions();

        this.initialPrivacyLevel = this.shareSettings.controls.privacyLevel.value.level;

        this.ngUnsubscribe.add(
          // Begin listening for changes to the form.
          this.onChange
            .pipe(
              catchError((err) => {
                throw err;
              }),
              tap(() => this.$rootScope.$emit('bulbAutoSave', {
                status: 'AUTO_SAVE_SAVING',
                restId: this.unitId,
              })),
            )
            .subscribe(
              (settingsFromServer: UnitPermissionsModel): void => {
                // We need to update the pageview autosave status to reflect the new permissions.
                // First, send a 'saved' event to update savingReferenceCount in autosavestatus-directive.
                this.$rootScope.$emit('bulbAutoSave', {
                  status: 'AUTO_SAVE_SAVED',
                  restId: this.unitId,
                });

                // Then, send an event based on the new permissions.
                if (['PUBLIC', 'CUSTOM'].includes(settingsFromServer.privacyLevel.level)) {
                  this.unitStateStore.transitionShareStatus(ShareTransition.SHARE);
                } else {
                  this.unitStateStore.transitionShareStatus(ShareTransition.MAKE_PRIVATE);
                }

                this.lastSettings = settingsFromServer;
              },
              (err) => {
                if (err.error.developerMessage === 'server_error_code_missing') {
                  return this.dialogService.showDialog({
                    icon: 'fa fa-exclamation-triangle',
                    iconClass: 'aero-alert',
                    title: 'Error Encountered',
                    detail: 'Unexpected Server Error, Hormel would be proud.',
                    showSecondary: false,
                    primaryLabel: 'Ok',
                  });
                }
                this.messagesSvc.showServerErrorDialog();
                console.error('Error saving permissions', err);
              },
            ),
        );
      });
  }

  private refreshUserAndUpdateSharePermissions(): void {
    this.BulbAuthService.fetchCurrentUser().then(() => {
      this.publishingFeature = this.BulbAuthService.currentUser.features.PublishingFeature;
      this.sharingFeature = this.BulbAuthService.currentUser.features.SharingFeature;

      this.updateUserSharePermissions();
    });
  }

  private updateUserSharePermissions(): void {
    const adminDisabledPublishing = !this.publishingFeature.publicPublishing;

    const { maxPublishedPages } = this.publishingFeature;
    const { numPublishedPages } = this.BulbAuthService.currentUser.metrics;
    // maxPublishedPages === -1 denotes "unlimited pages"
    const maxPublicPagesReached = maxPublishedPages > -1 && numPublishedPages >= maxPublishedPages;

    const { level, inherited } = this.shareSettings.getRawValue().privacyLevel;

    // Reset everything to enabled to start
    this.publicOption.disabled = false;
    this.customOption.disabled = false;
    this.privateOption.disabled = false;

    // Handle restrictions from inherited permissions.  The level cannot be set lower than the inherited level,
    // except for new units that start as private because they haven't been shared yet.
    if (inherited === 'PUBLIC') {
      this.customOption.disabled = true;
      this.customOption.disabledReason = 'disabled.inherited';

      // Disable the private option, but only if it isn't the currently selected option
      if (level !== 'PRIVATE') {
        this.privateOption.disabled = true;
        this.privateOption.disabledReason = 'disabled.inherited';
      }
    } else if (inherited === 'CUSTOM' && level !== 'PRIVATE') {
      // similar to inherited public, we want to disable private, but not if it is the currently selected option
      this.privateOption.disabled = true;
      this.privateOption.disabledReason = 'disabled.inherited';
    }

    if (adminDisabledPublishing) {
      // Admin has disabled public publishing, so turn off that option and say why
      this.publicOption.disabled = true;
      this.publicOption.disabledReason = 'disabled.admin';
    } else if (maxPublicPagesReached && level === 'PRIVATE') {
      // If the user has shared the max pages, disable sharing.  However, if the page is already shared (not private),
      // then the user is able to change the level, and so we don't disable have to disable anything.
      this.publicOption.disabled = true;
      this.publicOption.disabledReason = 'disabled.limitReached';
      this.customOption.disabled = true;
      this.customOption.disabledReason = 'disabled.limitReached';
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.unsubscribe();
  }
}
